import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Edit, MapPin, Fuel, Car, IdCard, Phone, Mail, DollarSign, Route, User } from 'lucide-react';
import { motoristasService } from '../../services/motoristas';
import { formatCurrency, formatDate, formatKilometers } from '../../utils/formatters';
import BackButton from '../../components/BackButton';
import { Veiculo, Viagem } from '../../types';

const statusMotorista: Record<string, string> = {
  ativo: 'bg-green-100 text-green-800',
  inativo: 'bg-gray-100 text-gray-800',
  suspenso: 'bg-red-100 text-red-800',
  licenciado: 'bg-yellow-100 text-yellow-800',
};

const cnhInfo = (vencimento?: string | null) => {
  if (!vencimento) return { label: 'Sem data', cls: 'bg-gray-100 text-gray-700' };
  const dias = Math.ceil((new Date(vencimento).getTime() - Date.now()) / 86_400_000);
  if (dias < 0) return { label: 'CNH vencida', cls: 'bg-red-100 text-red-800' };
  if (dias <= 30) return { label: `Vence em ${dias} dia(s)`, cls: 'bg-yellow-100 text-yellow-800' };
  return { label: 'CNH em dia', cls: 'bg-green-100 text-green-800' };
};

const iniciais = (nome: string) =>
  nome.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const Campo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-900">{children || '—'}</p>
  </div>
);

const StatCard: React.FC<{ icon: React.ElementType; label: string; value: React.ReactNode; color: string }> = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-lg shadow-sm border p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1a` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const MotoristaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['motorista', id],
    queryFn: () => motoristasService.getMotorista(Number(id)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="w-full">
        <BackButton to="/motoristas" label="Voltar para lista" className="mb-4" />
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
          Motorista não encontrado.
        </div>
      </div>
    );
  }

  const m = data.data;
  const est = (data.estatisticas || {}) as any;
  const cnh = cnhInfo(m.vencimento_cnh);
  const veiculos: Veiculo[] = m.veiculos ?? [];
  const viagens: Viagem[] = m.viagens ?? [];

  return (
    <div className="w-full space-y-6">
      <BackButton to="/motoristas" label="Voltar para lista" />

      {/* Cabeçalho do perfil */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {m.foto_url ? (
              <img src={m.foto_url} alt={m.nome} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-bold">
                {iniciais(m.nome)}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{m.nome}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusMotorista[m.status] || 'bg-gray-100 text-gray-800'}`}>
                  {m.status.charAt(0).toUpperCase() + m.status.slice(1)}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <IdCard className="w-3.5 h-3.5" /> CNH {m.categoria_cnh}
                </span>
                <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${cnh.cls}`}>{cnh.label}</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate(`/motoristas/${m.id}/editar`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 self-start"
          >
            <Edit className="w-4 h-4" /> Editar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Route} label="Viagens" value={est.total_viagens ?? 0} color="#0891b2" />
        <StatCard icon={MapPin} label="Km rodados" value={formatKilometers(est.km_total_rodados || 0)} color="#2563eb" />
        <StatCard icon={Fuel} label="Abastecimentos" value={est.total_abastecimentos ?? 0} color="#d97706" />
        <StatCard icon={DollarSign} label="Gasto combustível" value={formatCurrency(est.gasto_total_combustivel || 0)} color="#059669" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados pessoais */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><User className="w-5 h-5 text-gray-400" /> Dados pessoais</h2>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="CPF">{m.cpf}</Campo>
            <Campo label="RG">{m.rg}</Campo>
            <Campo label="Nascimento">{formatDate(m.data_nascimento)}</Campo>
            <Campo label="Estado civil">{m.estado_civil}</Campo>
            <Campo label="Sexo">{m.sexo === 'M' ? 'Masculino' : 'Feminino'}</Campo>
            <Campo label="Admissão">{formatDate(m.data_admissao)}</Campo>
            {m.salario != null && <Campo label="Salário">{formatCurrency(m.salario)}</Campo>}
            {m.data_demissao && <Campo label="Demissão">{formatDate(m.data_demissao)}</Campo>}
          </div>
        </div>

        {/* CNH / Documentos */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><IdCard className="w-5 h-5 text-gray-400" /> Habilitação (CNH)</h2>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Número da CNH">{m.cnh}</Campo>
            <Campo label="Categoria">{m.categoria_cnh}</Campo>
            <Campo label="Vencimento">{formatDate(m.vencimento_cnh)}</Campo>
            <Campo label="Primeira habilitação">{formatDate(m.primeira_habilitacao)}</Campo>
            <div>
              <p className="text-xs text-gray-500">Situação</p>
              <span className={`inline-flex mt-0.5 px-2.5 py-0.5 text-xs font-semibold rounded-full ${cnh.cls}`}>{cnh.label}</span>
            </div>
          </div>
        </div>

        {/* Contato e endereço */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Phone className="w-5 h-5 text-gray-400" /> Contato e endereço</h2>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Celular">{m.celular}</Campo>
            <Campo label="Telefone">{m.telefone}</Campo>
            <div className="col-span-2"><Campo label="E-mail"><span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" />{m.email || '—'}</span></Campo></div>
            <div className="col-span-2"><Campo label="Endereço">{`${m.endereco}, ${m.numero}${m.complemento ? ' - ' + m.complemento : ''} · ${m.bairro} · ${m.cidade}/${m.estado} · ${m.cep}`}</Campo></div>
          </div>
        </div>

        {/* Veículos vinculados */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Car className="w-5 h-5 text-gray-400" /> Veículos vinculados</h2>
          {veiculos.length === 0 ? (
            <p className="text-sm text-gray-400">Nenhum veículo vinculado.</p>
          ) : (
            <div className="space-y-2">
              {veiculos.map((v) => (
                <div
                  key={v.id}
                  onClick={() => navigate(`/veiculos/${v.id}`)}
                  className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{v.placa}</p>
                    <p className="text-xs text-gray-500">{v.marca} {v.modelo}</p>
                  </div>
                  <span className="text-xs text-gray-400">{v.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Viagens recentes */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Route className="w-5 h-5 text-gray-400" /> Viagens recentes</h2>
        {viagens.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhuma viagem registrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2">Rota</th>
                  <th className="px-3 py-2 text-right">Km</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {viagens.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/viagens/${v.id}`)}
                    className="cursor-pointer hover:bg-gray-50 text-sm"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{v.data_saida ? formatDate(v.data_saida) : '—'}</td>
                    <td className="px-3 py-2 text-gray-900">{v.origem} → {v.destino}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{v.km_percorridos ? formatKilometers(v.km_percorridos) : '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{String(v.status).replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MotoristaDetail;
